import { useState, useRef, useEffect } from 'react';
import { fmt, fmtN, MultiLineChart, WeekSummaryGrid, isActiveStage } from './plShared';
import { DealModal, DrillModal, useDrill } from './DrillModal';
import { MovementTable, enrichMovement } from './DealMovement';
import { API_URL } from '../../config/api';

const ACTIVE_STAGES = [
  '5% - Prospecting', '20%-Discovery', '40%-Scoping',
  '60%-Propose', '80%-Validate', '90%-Negotiate & Close',
];

const STAGE_SHORT = {
  '5% - Prospecting': '5% Prosp', '20%-Discovery': '20% Disc',
  '40%-Scoping': '40% Scope', '60%-Propose': '60% Prop',
  '80%-Validate': '80% Val', '90%-Negotiate & Close': '90% Neg',
};

const STAGE_COLORS = {
  '5% - Prospecting': '#7c3aed', '20%-Discovery': '#2563eb',
  '40%-Scoping': '#0d9488', '60%-Propose': '#d97706',
  '80%-Validate': '#ea580c', '90%-Negotiate & Close': '#dc2626',
};

const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const money = value => fmt(number(value));

function TrendCard({ title, tag, children, onClick, clickHint }) {
  return (
    <section className={`pl-card${onClick ? ' pl-card-clickable' : ''}`} onClick={onClick}>
      <div className="pl-card-header" style={{ display: 'block' }}>
        <div className="pl-card-title">{title}</div>
        {tag && <div className="pl-card-sub">{tag}</div>}
      </div>
      {children}
      {onClick && <div className="ch">{clickHint || '🔍 Click to drill into deals'}</div>}
    </section>
  );
}

function TrendChart({ weeklyTrend = [], aop = 0, target = 0, onWeekClick }) {
  if (!weeklyTrend.length) return null;
  const width = 700;
  const height = 280;
  const pad = { left: 50, right: 66, top: 14, bottom: 26 };
  const max = Math.max(...weeklyTrend.flatMap(row => [number(row.active), number(row.weighted), number(row.won)]), aop, target, 1);
  const x = index => pad.left + index / (weeklyTrend.length - 1 || 1) * (width - pad.left - pad.right);
  const y = value => pad.top + (1 - number(value) / max) * (height - pad.top - pad.bottom);
  const line = key => weeklyTrend.map((row, index) => `${x(index)},${y(row[key])}`).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="pl-trend-svg" role="img" aria-label="Full pipeline trend">
      {[0, .25, .5, .75, 1].map(ratio => (
        <g key={ratio}>
          <line x1={pad.left} y1={y(max * ratio)} x2={width - pad.right} y2={y(max * ratio)} stroke="#d1d5db" />
          <text x={pad.left - 8} y={y(max * ratio) + 4} textAnchor="end" fontSize="9" fill="#6b7280">{money(max * ratio)}</text>
        </g>
      ))}
      {aop > 0 && <><line x1={pad.left} y1={y(aop)} x2={width - pad.right} y2={y(aop)} stroke="#d97706" strokeDasharray="5 4" /><text x={width - pad.right + 5} y={y(aop) + 4} fontSize="9" fill="#d97706">AOP</text></>}
      {target > 0 && <><line x1={pad.left} y1={y(target)} x2={width - pad.right} y2={y(target)} stroke="#dc2626" strokeDasharray="5 4" /><text x={width - pad.right + 5} y={y(target) + 4} fontSize="9" fill="#dc2626">Target</text></>}
      <polyline points={line('active')} fill="none" stroke="#0891b2" strokeWidth="2.5" strokeLinejoin="round" />
      <polyline points={line('weighted')} fill="none" stroke="#7c3aed" strokeWidth="2" strokeDasharray="4 3" strokeLinejoin="round" />
      <polyline points={line('won')} fill="none" stroke="#059669" strokeWidth="2" strokeLinejoin="round" />
      {weeklyTrend.map((row, index) => (
        <g key={`${row.week || row.week_num}-${index}`}>
          <circle cx={x(index)} cy={y(row.active)} r="3.5" fill="#0891b2" role="button" tabIndex="0" onClick={event => { event.stopPropagation(); onWeekClick(row); }} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.stopPropagation(); onWeekClick(row); } }}>
            <title>{`${row.week || `W${row.week_num}`}: ${money(row.active)}`}</title>
          </circle>
          <text x={x(index)} y={height - 5} textAnchor="middle" fontSize="8.5" fill="#6b7280">W{row.week_num}</text>
        </g>
      ))}
    </svg>
  );
}

function WoWChart({ weeklyTrend = [], onWeekClick }) {
  if (weeklyTrend.length < 2) return null;
  const changes = weeklyTrend.map((row, index) => ({ ...row, value: index ? number(row.active) - number(weeklyTrend[index - 1].active) : 0 }));
  const maxAbs = Math.max(...changes.map(row => Math.abs(row.value)), 1);
  const width = 700;
  const height = 160;
  const pad = { left: 46, right: 10, top: 10, bottom: 24 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const mid = pad.top + innerHeight / 2;
  const barWidth = innerWidth / changes.length * .65;
  const signedMoney = value => value < 0 ? `-${money(Math.abs(value))}` : money(value);
  const ticks = [1, .5, 0, -.5, -1];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="pl-trend-svg" role="img" aria-label="Week over week pipeline change">
      {ticks.map(ratio => {
        const y = mid - ratio * (innerHeight / 2 - 2);
        return <g key={ratio}>
          <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} stroke={ratio === 0 ? '#9ca3af' : '#e5e7eb'} />
          <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="8" fill="#6b7280">{signedMoney(maxAbs * ratio)}</text>
        </g>;
      })}
      {changes.map((row, index) => {
        const x = pad.left + (index + .5) * innerWidth / changes.length;
        const barHeight = Math.max(Math.abs(row.value) / maxAbs * (innerHeight / 2 - 2), 1);
        const y = row.value >= 0 ? mid - barHeight : mid;
        return <g key={index} role="button" tabIndex="0" onClick={event => { event.stopPropagation(); onWeekClick?.(row); }} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.stopPropagation(); onWeekClick?.(row); } }}>
          <rect x={x - barWidth / 2} y={y} width={barWidth} height={barHeight} fill={row.value >= 0 ? '#059669' : '#dc2626'} opacity=".85" rx="1" />
          <text x={x} y={height - 4} textAnchor="middle" fontSize="8.5" fill="#6b7280">W{row.week_num}</text>
        </g>;
      })}
    </svg>
  );
}

function CountChart({ weeklyTrend = [], onWeekClick }) {
  if (!weeklyTrend.length) return null;
  const totals = weeklyTrend.map(row => Math.max(number(row.active_count_all), number(row.count)));
  const max = Math.max(...totals, 1);
  const width = 700;
  const height = 160;
  const pad = { left: 28, right: 10, top: 10, bottom: 24 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const barWidth = innerWidth / weeklyTrend.length * .65;
  return <svg viewBox={`0 0 ${width} ${height}`} className="pl-trend-svg" role="img" aria-label="Active deal count by week, with value vs no value">
    {[0, .25, .5, .75, 1].map(ratio => {
      const y = pad.top + innerHeight * (1 - ratio);
      return <g key={ratio}>
        <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} stroke="#e5e7eb" />
        <text x={pad.left - 6} y={y + 3} textAnchor="end" fontSize="8" fill="#6b7280">{Math.round(max * ratio)}</text>
      </g>;
    })}
    {weeklyTrend.map((row, index) => {
      const x = pad.left + (index + .5) * innerWidth / weeklyTrend.length;
      const withValue = number(row.count);
      const withoutValue = Math.max(0, number(row.active_count_all) - withValue);
      const withValueHeight = withValue / max * innerHeight;
      const withoutValueHeight = withoutValue / max * innerHeight;
      const barBottom = pad.top + innerHeight;
      return (
        <g
          key={index}
          role="button"
          tabIndex="0"
          style={{ cursor: onWeekClick ? 'pointer' : 'default' }}
          onClick={event => { event.stopPropagation(); onWeekClick?.(row); }}
          onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.stopPropagation(); onWeekClick?.(row); } }}
        >
          <title>{`${row.week || `W${row.week_num}`}: ${withValue} with value · ${withoutValue} no value`}</title>
          <rect x={x - barWidth / 2} y={barBottom - withValueHeight} width={barWidth} height={withValueHeight} fill="#0891b2" opacity=".85" />
          <rect x={x - barWidth / 2} y={barBottom - withValueHeight - withoutValueHeight} width={barWidth} height={withoutValueHeight} fill="#d97706" opacity=".85" />
          <text x={x} y={height - 4} textAnchor="middle" fontSize="8.5" fill="#6b7280">W{row.week_num}</text>
        </g>
      );
    })}
  </svg>;
}

function StackedChart({ weeklyStage = [], onSegmentClick }) {
  if (!weeklyStage.length) return null;
  const max = Math.max(...weeklyStage.map(row => ACTIVE_STAGES.reduce((sum, stage) => sum + number(row.stages?.[stage]), 0)), 1);
  const width = 700;
  const height = 220;
  const pad = { left: 50, right: 10, top: 10, bottom: 24 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const barWidth = innerWidth / weeklyStage.length * .7;
  return <svg viewBox={`0 0 ${width} ${height}`} className="pl-trend-svg" role="img" aria-label="Stage evolution by week">
    {[0, .25, .5, .75, 1].map(ratio => (
      <g key={ratio}>
        <line x1={pad.left} y1={pad.top + innerHeight * (1 - ratio)} x2={width - pad.right} y2={pad.top + innerHeight * (1 - ratio)} stroke="#e5e7eb" />
        <text x={pad.left - 8} y={pad.top + innerHeight * (1 - ratio) + 4} textAnchor="end" fontSize="9" fill="#6b7280">{money(max * ratio)}</text>
      </g>
    ))}
    {weeklyStage.map((row, weekIndex) => {
      const x = pad.left + (weekIndex + .5) * innerWidth / weeklyStage.length;
      let bottom = pad.top + innerHeight;
      return <g key={weekIndex}>
        {ACTIVE_STAGES.map(stage => {
          const value = number(row.stages?.[stage]);
          if (!value) return null;
          const barHeight = value / max * innerHeight;
          bottom -= barHeight;
          const handleClick = event => { event.stopPropagation(); onSegmentClick(row, stage); };
          return <rect key={stage} x={x - barWidth / 2} y={bottom} width={barWidth} height={barHeight} fill={STAGE_COLORS[stage]} opacity=".85" role="button" tabIndex="0" style={{ cursor: 'pointer' }} onClick={handleClick} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') handleClick(event); }}><title>{`${row.week || `W${row.week_num}`} — ${STAGE_SHORT[stage]}: ${money(value)}`}</title></rect>;
        })}
        <text x={x} y={height - 4} textAnchor="middle" fontSize="8.5" fill="#6b7280">W{row.week_num}</text>
      </g>;
    })}
  </svg>;
}


function PipelineTrend({ data, fetchWeekDeals }) {
  const { weekly_trend = [], weekly_stage_trend = [], stage_dist = [], kpis = {}, executive = {}, deals = [] } = data || {};
  const { drill, activeDeal, openDrill, closeDrill, openDeal, closeDeal, drillStage } = useDrill();

  // Landmark comparison weeks for the Stage Movement card: W1, then every
  // 10th week (W10, W20, W30…), always including the latest available week
  // so the picker keeps extending as more weeks of data come in.
  const maxWeekNum = weekly_trend[weekly_trend.length - 1]?.week_num || 1;
  const landmarkNums = [1];
  for (let n = 10; n <= maxWeekNum; n += 10) landmarkNums.push(n);
  const weekByNum = Object.fromEntries(weekly_trend.map(row => [row.week_num, row]));
  const landmarkWeeks = landmarkNums.map(n => weekByNum[n]).filter(Boolean);
  const latestTrend = weekly_trend[weekly_trend.length - 1] || {};
  if (latestTrend.week && !landmarkWeeks.some(row => row.week_num === latestTrend.week_num)) {
    landmarkWeeks.push(latestTrend);
  }
  // Stage Movement week filter: pick any number of weeks from a dropdown
  // (not just one landmark week) — each selected week gets its own column
  // in the table, same multi-select pattern as the other scorecards.
  const [cmpWeeks, setCmpWeeks] = useState(() => landmarkWeeks.map(row => row.week));
  const [cmpDropdownOpen, setCmpDropdownOpen] = useState(false);
  const cmpDropdownRef = useRef(null);
  useEffect(() => {
    if (!cmpDropdownOpen) return;
    const close = event => { if (cmpDropdownRef.current && !cmpDropdownRef.current.contains(event.target)) setCmpDropdownOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [cmpDropdownOpen]);

  if (!weekly_trend.length) return <div className="pl-empty"><p>No trend data available.</p></div>;

  const toggleCmpWeek = week => {
    setCmpWeeks(current => current.includes(week) ? current.filter(w => w !== week) : [...current, week]);
  };
  const cmpSelectedRows = cmpWeeks
    .map(w => weekly_trend.find(row => row.week === w))
    .filter(Boolean)
    .sort((a, b) => a.week_num - b.week_num);

  const metricDeals = deals.filter(deal => number(deal.amount) > 0);
  const wonDeals = metricDeals.filter(deal => deal.stage === 'Business Won');
  const selectedWeek = executive.selected_week_short || `W${latestTrend.week_num || '?'}`;
  const currentStageDist = Object.fromEntries(stage_dist.map(item => [item.stage, number(item.amount)]));
  const current = executive.period_comparison?.current || latestTrend;
  const currentWeekFull = current.week || latestTrend.week;
  // Looks up one stage's value for one specific historical week — Won comes
  // off the weekly_trend row, every other stage off weekly_stage_trend.
  const stageValueForWeek = (weekFull, stage) => {
    if (stage === 'Business Won') return number(weekly_trend.find(row => row.week === weekFull)?.won);
    return number(weekly_stage_trend.find(row => row.week === weekFull)?.stages?.[stage]);
  };
  const openWeekDeals = async (row) => {
    const weekLabel = row.week || `W${row.week_num}`;
    openDrill(`${weekLabel} Active Pipeline`, 'Loading…', null, <div className="pl-chart-empty">Loading {weekLabel} deals…</div>);
    try {
      const raw = await fetchWeekDeals(row.week);
      const weekActive = raw.filter(deal => number(deal.amount) > 0 && isActiveStage(deal.stage));
      const totalUW = weekActive.reduce((sum, deal) => sum + number(deal.amount), 0);
      const totalW = weekActive.reduce((sum, deal) => sum + number(deal.weighted), 0);
      openDrill(`${weekLabel} Active Pipeline`, `${weekActive.length} deals · ${money(totalUW)} UW · ${money(totalW)} Weighted · sorted by amount`, weekActive);
    } catch (err) {
      openDrill(`${weekLabel} Active Pipeline`, '', null, <div className="pl-chart-empty">Could not load {weekLabel}&rsquo;s deals.</div>);
    }
  };
  const openTrendSummary = () => openDrill('Pipeline Trend — Click a week to see deals', `${weekly_trend.length} weeks`, null, <WeekSummaryGrid weeklyTrend={weekly_trend} onWeekClick={openWeekDeals} />);
  const openWeekChange = async (row) => {
    const toWeek = row.week || `W${row.week_num}`;
    const index = weekly_trend.findIndex(item => item.week_num === row.week_num);
    const prevRow = index > 0 ? weekly_trend[index - 1] : null;
    if (!prevRow) {
      openDrill(`${toWeek} — Week-over-Week Change`, null, null, <div className="pl-chart-empty">No prior week available to compare {toWeek} against.</div>);
      return;
    }
    const fromWeek = prevRow.week || `W${prevRow.week_num}`;
    openDrill(`${fromWeek} → ${toWeek} — What Changed`, 'Loading…', null, <div className="pl-chart-empty">Loading changes…</div>);
    try {
      const params = new URLSearchParams({ from_week: fromWeek, to_week: toWeek });
      const res = await fetch(`${API_URL}/api/pipeline/movement/?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Movement request failed (${res.status})`);
      const d = await res.json();
      const enriched = enrichMovement(d.movement, deals);
      const moves = [...enriched.forward, ...enriched.backward, ...enriched.won, ...enriched.lost];
      const newCount = enriched.new.length;
      const sub = `${enriched.forward.length} moved forward · ${enriched.backward.length} moved back · ${enriched.won.length} won · ${enriched.lost.length} lost${newCount ? ` · ${newCount} new deals` : ''}`;
      openDrill(`${fromWeek} → ${toWeek} — What Changed`, sub, null, <MovementTable moves={moves} onDealClick={openDeal} />);
    } catch (err) {
      openDrill(`${fromWeek} → ${toWeek} — What Changed`, '', null, <div className="pl-chart-empty">Could not load week-over-week changes.</div>);
    }
  };
  const openDealCountBreakdown = (row) => {
    const weekLabel = row.week || `W${row.week_num}`;
    const withValueCount = number(row.count);
    const withoutValueCount = Math.max(0, number(row.active_count_all) - withValueCount);
    const openFiltered = async (title, predicate, emptyNote, includeZero) => {
      openDrill(title, 'Loading…', null, <div className="pl-chart-empty">Loading {weekLabel} deals…</div>);
      try {
        const raw = await fetchWeekDeals(row.week, { includeZero });
        const rows = raw.filter(deal => isActiveStage(deal.stage) && predicate(number(deal.amount)));
        openDrill(title, `${rows.length} deals · ${emptyNote}`, rows);
      } catch (err) {
        openDrill(title, '', null, <div className="pl-chart-empty">Could not load {weekLabel}&rsquo;s deals.</div>);
      }
    };
    const openWithValue = () => openFiltered(`${weekLabel} — With Value Active Deals`, amount => amount > 0, 'sorted by amount', false);
    // The dashboard API always excludes zero-amount rows by default — this
    // is the one drill that needs them, so it explicitly asks for them.
    const openWithoutValue = () => openFiltered(`${weekLabel} — No Value Active Deals`, amount => amount <= 0, 'placeholder rows with no dollar amount', true);
    openDrill(`${weekLabel} — Active Deal Count Breakdown`, 'Click a card to see those deals', null, (
      <div className="krow" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <div className="kc kb" onClick={openWithValue} style={{ cursor: 'pointer' }}>
          <span className="kl">With Value Deals</span>
          <span className="kv">{withValueCount}</span>
          <span className="kh">Active deals with a dollar amount</span>
          <span className="click-hint">🔍 Click → see deals</span>
        </div>
        <div className="kc ka" onClick={openWithoutValue} style={{ cursor: 'pointer' }}>
          <span className="kl">No Value Deals</span>
          <span className="kv">{withoutValueCount}</span>
          <span className="kh">Active deals with no dollar amount (placeholders)</span>
          <span className="click-hint">🔍 Click → see deals</span>
        </div>
      </div>
    ));
  };
  const openWon = () => openDrill(`Won Deals — ${wonDeals.length} Deals`, 'All won deals · Click any row for full detail', wonDeals);
  // Stage Movement cells fetch their own week's snapshot, so a click always
  // shows that specific week's deals instead of whatever week happens to be
  // globally selected elsewhere on the dashboard.
  const openStageForWeek = async (weekFull, stage, label) => {
    openDrill(`${weekFull} — ${label}`, 'Loading…', null, <div className="pl-chart-empty">Loading {weekFull} deals…</div>);
    try {
      const raw = await fetchWeekDeals(weekFull);
      const rows = raw.filter(deal => deal.stage === stage);
      openDrill(`${weekFull} — ${label}`, `${rows.length} deals · Click any row for full detail`, rows);
    } catch (err) {
      openDrill(`${weekFull} — ${label}`, '', null, <div className="pl-chart-empty">Could not load {weekFull}&rsquo;s deals.</div>);
    }
  };

  const stageRows = [
    { stage: 'Business Won', label: '🏆 Won', current: number(current.won) },
    ...[...ACTIVE_STAGES].reverse().map(stage => ({
      stage,
      label: STAGE_SHORT[stage] || stage,
      current: number(currentStageDist[stage]),
    })),
  ];

  return <>
    <TrendCard title="Full Pipeline Trend ↗ Click any point → that week's deals" tag={`Historical weekly snapshots · All pipeline data · ${weekly_trend.length} weeks`} onClick={openTrendSummary} clickHint="🔍 Click any point → that week's deals">
      <TrendChart weeklyTrend={weekly_trend} aop={number(kpis.aop)} target={number(kpis.sales_target)} onWeekClick={openWeekDeals} />
      <div className="pl-chart-legend"><span>— Won Cumulative</span><span>- - Weighted Pipeline</span><span>- - AOP</span></div>
    </TrendCard>

    <div className="g2">
      <TrendCard title="Week-over-Week Change ($M) — Unweighted ↗ Click bar → what changed" tag="Green = growth · Red = decline · Click a bar to see that week's deal-level changes vs the prior week" clickHint="🔍 Click bar → what changed"><WoWChart weeklyTrend={weekly_trend} onWeekClick={openWeekChange} /></TrendCard>
      <TrendCard title="Active Deal Count by Week ↗ Click a week" tag="Total deals tracked per snapshot — with value vs no value">
        <CountChart weeklyTrend={weekly_trend} onWeekClick={openDealCountBreakdown} />
        <div className="pl-chart-legend"><span style={{ color: '#0891b2' }}>■ With Value</span><span style={{ color: '#d97706' }}>■ No Value</span></div>
      </TrendCard>
    </div>

    <div className="g2">
      <TrendCard title="Won Cumulative ($M) ↗ Click → won deals" tag="Running total of won pipeline">
        <MultiLineChart series={[
          { data: weekly_trend.map(row => ({ value: row.won, label: `W${row.week_num}` })), color: '#059669', onClick: openWon },
        ]} height={180} />
        <div className="pl-chart-legend">
          <span className="won" onClick={openWon} style={{ cursor: 'pointer' }}>— Won</span>
        </div>
      </TrendCard>
      <TrendCard title={`Stage Movement → ${selectedWeek}`} tag="Pipeline value change by stage — click any week's value to see that week's deals">
        <div className="sc-week-filter" ref={cmpDropdownRef}>
          <span className="sc-week-filter-label">📅 Filter by Week</span>
          <div className="sc-week-dropdown">
            <button type="button" className="sc-week-dropdown-btn" onClick={() => setCmpDropdownOpen(open => !open)} aria-haspopup="listbox" aria-expanded={cmpDropdownOpen}>
              {cmpWeeks.length ? `${cmpWeeks.length} week${cmpWeeks.length === 1 ? '' : 's'} selected` : 'Select weeks'}
              <span className="sc-week-dropdown-arrow">{cmpDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {cmpDropdownOpen && (
              <div className="sc-week-dropdown-panel" role="listbox" aria-multiselectable="true">
                {weekly_trend.map(row => (
                  <label className="sc-week-dropdown-option" key={row.week}>
                    <input type="checkbox" checked={cmpWeeks.includes(row.week)} onChange={() => toggleCmpWeek(row.week)} />
                    W{row.week_num}{row.week_num === latestTrend.week_num ? ' (Latest)' : ''}
                  </label>
                ))}
              </div>
            )}
          </div>
          <span className="sc-week-filter-hint">Click to open, check the weeks you want to compare</span>
        </div>
        <div className="pl-twrap" style={{ marginTop: 8 }}><table><thead><tr>
          <th>Stage</th>
          {cmpSelectedRows.map(row => <th key={row.week}>W{row.week_num}</th>)}
          <th>{selectedWeek}</th><th>Change</th>
        </tr></thead><tbody>
          {stageRows.map(row => {
            const earliestRow = cmpSelectedRows[0];
            const earliestVal = earliestRow ? stageValueForWeek(earliestRow.week, row.stage) : null;
            const difference = earliestVal !== null ? row.current - earliestVal : null;
            return (
              <tr key={row.stage}>
                <td>{row.label}</td>
                {cmpSelectedRows.map(weekRow => (
                  <td
                    className="td2 pl-bm-week-cell"
                    key={weekRow.week}
                    title={`See W${weekRow.week_num} ${row.label} deals`}
                    onClick={() => openStageForWeek(weekRow.week, row.stage, row.label)}
                  >
                    {fmtN(stageValueForWeek(weekRow.week, row.stage))}
                  </td>
                ))}
                <td className="td2 pl-click-row" style={{ cursor: 'pointer' }} title={`See ${selectedWeek} ${row.label} deals`} onClick={() => openStageForWeek(currentWeekFull, row.stage, row.label)}>{fmtN(row.current)}</td>
                <td>{difference === null ? '—' : <span className={difference >= 0 ? 'up' : 'dn'}>{difference >= 0 ? `+${fmtN(difference)}` : `−${fmtN(Math.abs(difference))}`}</span>}</td>
              </tr>
            );
          })}
        </tbody></table></div>
        <div className="pl-click-hint">🔍 Click a week's value → see that week's stage deals</div>
      </TrendCard>
    </div>

    <TrendCard title="Stage Evolution — Stacked Pipeline by Week ↗ Click a segment → that week's stage deals" tag={`Pipeline value by stage across ${weekly_stage_trend.length} weeks`}>
      <StackedChart weeklyStage={weekly_stage_trend} onSegmentClick={(row, stage) => openStageForWeek(row.week, stage, STAGE_SHORT[stage] || stage)} />
      <div className="pl-chart-legend">{ACTIVE_STAGES.map(stage => <span key={stage}><i style={{ background: STAGE_COLORS[stage] }} />{STAGE_SHORT[stage]}</span>)}</div>
    </TrendCard>

    {drill && <DrillModal title={drill.title} sub={drill.sub} deals={drill.deals} totalField={drill.totalField} totalLabel={drill.totalLabel} onClose={closeDrill} onDealClick={openDeal}>{drill.children}</DrillModal>}
    {activeDeal && <DealModal deal={activeDeal} onClose={closeDeal} onDrillStage={stage => drillStage(stage, metricDeals)} />}
  </>;
}

export { PipelineTrend };
export default PipelineTrend;
